import { defineStore } from 'pinia';
import { ref } from 'vue';

import {
  listNotifications,
  markRead as markReadApi,
  type NotificationDto,
} from '@/api/notifications';

const POLL_INTERVAL_MS = 30_000;

/**
 * Per ADR-0036 §6. Polls the notifications endpoint every 30s while the
 * AppShell is mounted. Drives the bell-and-dot in AppTopbar. No SSE/WS
 * infra in the SPA today — polling is the pragmatic v1.
 *
 * The store is a singleton via Pinia; `start()` is idempotent so multiple
 * mount points that call it are safe.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const list = ref<NotificationDto[]>([]);
  const unreadCount = ref(0);
  const lastError = ref<string | null>(null);
  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight: Promise<void> | null = null;

  async function refresh(): Promise<void> {
    if (inFlight) return inFlight;
    inFlight = (async () => {
      try {
        const res = await listNotifications({ onlyUnread: true, limit: 20 });
        list.value = res.notifications;
        unreadCount.value = res.notifications.filter((n) => !n.readAt).length;
        lastError.value = null;
      } catch (err: unknown) {
        // Likely a 401 between polls — silently keep prior state. Don't
        // spam the console; the bell just stays at its previous value
        // until the next successful refresh.
        lastError.value = err instanceof Error ? err.message : String(err);
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  function start(): void {
    if (timer !== null) return;
    void refresh();
    timer = setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  }

  async function markRead(id: string): Promise<void> {
    try {
      await markReadApi(id);
    } finally {
      // Optimistic local update either way — the server is authoritative
      // on next refresh.
      list.value = list.value.map((n) =>
        n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
      );
      unreadCount.value = list.value.filter((n) => !n.readAt).length;
    }
  }

  return { list, unreadCount, lastError, refresh, start, stop, markRead };
});
